import { AccountSchema } from '../data/types'

/** localStorage flag set after a successful OTP so refresh can restore a verified session. */
export const AUTH_VERIFIED_STORAGE_KEY = 'auth_verified'

/** Compact account fields persisted so refresh can restore without a successful API body. */
export const AUTH_USER_SNAPSHOT_KEY = 'auth_user_snapshot'

export type AuthUserSnapshot = Pick<
  AccountSchema,
  'username' | 'email' | 'first_name' | 'last_name' | 'primary_community' | 'auth_status'
>

/**
 * Decodes a JWT payload that may use base64url (Cognito) instead of standard base64.
 * Returns null when the token is missing or not a readable JWT.
 */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  if (!token) {
    return null
  }
  const payload = token.split('.')[1]
  if (!payload) {
    return null
  }
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
      payload.length + (4 - payload.length % 4) % 4,
      '='
    )
    const parsed = JSON.parse(atob(padded))
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null
  } catch {
    return null
  }
}

/** Reads the Cognito `username` claim from a stored access token, or empty string. */
export const getUsernameFromAccessToken = (token: string): string => {
  const payload = decodeJwtPayload(token)
  if (payload && typeof payload.username === 'string' && payload.hasOwnProperty('exp')) {
    return payload.username
  }
  return ''
}

/** Reads whether this browser already completed OTP for the stored JWT. */
export const readAuthVerified = (): boolean => {
  if (typeof localStorage === 'undefined') {
    return false
  }
  return localStorage.getItem(AUTH_VERIFIED_STORAGE_KEY) === 'true'
}

/** Marks the current browser session as OTP-verified. */
export const persistAuthVerified = (): void => {
  if (typeof localStorage === 'undefined') {
    return
  }
  localStorage.setItem(AUTH_VERIFIED_STORAGE_KEY, 'true')
}

/** Persists a compact verified account so refresh can render AuthView if the API body is empty. */
export const persistAuthSnapshot = (user?: AccountSchema | null): void => {
  if (typeof localStorage === 'undefined' || !user?.username) {
    return
  }
  const snapshot: AuthUserSnapshot = {
    username: user.username,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    primary_community: user.primary_community,
    auth_status: true,
  }
  localStorage.setItem(AUTH_USER_SNAPSHOT_KEY, JSON.stringify(snapshot))
}

/** Reads the last verified account snapshot, or null. */
export const readAuthSnapshot = (): AuthUserSnapshot | null => {
  if (typeof localStorage === 'undefined') {
    return null
  }
  try {
    const raw = localStorage.getItem(AUTH_USER_SNAPSHOT_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as AuthUserSnapshot
    return parsed && typeof parsed.username === 'string' ? parsed : null
  } catch {
    return null
  }
}

/** Writes JWTs independently so a missing refresh_token still keeps the access token. */
export const persistAuthTokens = (loginToken?: string, refreshToken?: string): void => {
  if (typeof localStorage === 'undefined') {
    return
  }
  if (loginToken) {
    localStorage.setItem('access_token', loginToken)
  }
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken)
  }
}

/** Persists OTP-verified flag, account snapshot, and any JWTs on the auth payload. */
export const persistVerifiedSession = (user?: AccountSchema | null): void => {
  persistAuthVerified()
  persistAuthSnapshot(user)
  persistAuthTokens(user?.login_token, user?.refresh_token)
}

/** Clears the OTP-verified flag and snapshot (new login or sign-out). */
export const clearAuthVerified = (): void => {
  if (typeof localStorage === 'undefined') {
    return
  }
  localStorage.removeItem(AUTH_VERIFIED_STORAGE_KEY)
  localStorage.removeItem(AUTH_USER_SNAPSHOT_KEY)
}

/**
 * Returns whether a token restore should treat the user as OTP-verified.
 * The test API can return auth_status false for a valid JWT; honor a prior local verify in that case.
 */
export const isRestoredSessionVerified = (
  authStatus: unknown,
  locallyVerified: boolean
): boolean => !!(authStatus || locallyVerified)

/** Username from the stored JWT, or the snapshot when the API omitted a persistable refresh token. */
export const getRestoreUsername = (accessToken: string): string => (
  getUsernameFromAccessToken(accessToken) || readAuthSnapshot()?.username || ''
)

/** True when a prior OTP/snapshot exists and we have a username from the JWT or snapshot. */
export const canRestoreVerifiedSession = (tokenUsername: string): boolean => {
  const snapshotUsername = readAuthSnapshot()?.username || ''
  return !!(readAuthVerified() || snapshotUsername) && !!(tokenUsername || snapshotUsername)
}
